import type { PaginatedResponse } from "@/features/profiles/services/profiles/types";
import type { ReceivableDocument } from "./receivable-document.types";
import type { ReceivableTax } from "./receivable-tax.types";

export enum ReceivableCurrency {
  BRL = "BRL",
  USD = "USD",
}

export enum ReceivableStatus {
  WAITING = "WAITING",
  RECEIVED = "RECEIVED",
  CONCLUDED = "CONCLUDED",
  CALCULATING = "CALCULATING",
  CALCULATED = "CALCULATED",
  FAIL = "FAIL",
}

export enum ReceivableStore {
  APPLE = "APPLE",
  GOOGLE = "GOOGLE",
  SAMSUNG = "SAMSUNG",
  STRIPE = "STRIPE",
}

export interface Receivable {
  id: string; // uuid
  currency: ReceivableCurrency;
  status: ReceivableStatus;
  store: ReceivableStore;
  grossValue: number; // valor bruto
  netValue?: number; // valor líquido (grossValue - taxas) - calculado pelo backend
  receivedValue?: number; // valor líquido (calculado) - somente resposta

  // Campos retornados pela API (nomes reais)
  receivableDocuments?: ReceivableDocument[];
  receivableTaxes?: ReceivableTax[];
  receivableCreatorSnapshots?: unknown[];

  // Datas — API usa formato "YYYY-MM" no POST e retorna ISO no GET
  relatedDate?: string; // "2025-01-01T00:00:00.000Z" (GET) ou "2025-01" (POST)
  paidDate?: string; // "2025-03-01T00:00:00.000Z" (GET) ou "2025-03" (POST)

  // Campos computados no front para uso no formulário
  relatedMonth?: number; // extraído de relatedDate
  relatedYear?: number; // extraído de relatedDate

  receivableShareConfigId?: string;
  receivableShareConfig?: {
    id: string;
    name?: string | null;
  };

  createdBy?: string; // uuid
  updatedBy?: string; // uuid
  createdAt: string; // ISO datetime
  updatedAt?: string; // ISO datetime
  lastUpdatedAt?: string; // campo real da API
  file?: File;
}

export type ReceivableSortBy = 'relatedDate' | 'grossValue' | 'createdAt';
export type ReceivableOrder = 'asc' | 'desc';

export interface GetReceivablesParams {
  page?: number;
  limit?: number;
  status?: ReceivableStatus | 'all';
  currency?: ReceivableCurrency | 'all';
  store?: ReceivableStore | 'all';
  relatedDateStart?: string;
  relatedDateEnd?: string;
  paidDateStart?: string;
  paidDateEnd?: string;
  shareConfigId?: string;
  createdBy?: string;
  search?: string;
  sortBy?: ReceivableSortBy;
  order?: ReceivableOrder;
}

export type PaginatedReceivablesResponse = PaginatedResponse<Receivable>;

// Payload de criação — relatedDate e paidDate em formato "YYYY-MM"
export interface CreateReceivablePayload {
  currency: ReceivableCurrency;
  status: ReceivableStatus;
  store: ReceivableStore;
  grossValue: number;
  relatedDate: string; // "YYYY-MM" ex: "2025-01"
  paidDate?: string; // "YYYY-MM" ex: "2025-03"
  receivableShareConfigId: string;
}

export interface UpdateReceivablePayload {
  status?: ReceivableStatus;
}
